import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { colors } from '../../styles/theme';
import searchService, { type SearchResults } from '../../services/search.service';
import { humanize } from '../../lib/stringCase';
import { t } from '../../i18n/language';
import { getApiErrorMessage } from '../../lib/apiError';

interface GlobalSearchProps {
  role: string;
}

/** Returns the role-based URL prefix: agents use /agent/*, everyone else /manager/* */
function rolePrefix(role: string): string {
  return role === 'agent' ? 'agent' : 'manager';
}

const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.userAgent);

const GlobalSearch = ({ role }: GlobalSearchProps): JSX.Element => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // Kept separate from `results` so a failed search can never be read as
  // "nothing matched".
  const [searchError, setSearchError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Close on route change
  useEffect(() => {
    setOpen(false);
    setQuery('');
    setResults(null);
  }, [location.pathname]);

  // Cmd/Ctrl+K global shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Click-outside closes
  useEffect(() => {
    const handler = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced fetch
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults(null);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await searchService.globalSearch(trimmed);
        setResults(data);
        setSearchError(null);
      } catch (err) {
        // Previously `setResults(null)`, which renders identically to "nothing
        // matched" — so a failed search looked like proof a record does not
        // exist. Staff act on that, which makes it a correctness problem.
        setResults(null);
        setSearchError(getApiErrorMessage(err, 'Search is unavailable right now.'));
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const prefix = rolePrefix(role);

  function navigate_to(path: string): void {
    navigate(path);
    setOpen(false);
    setQuery('');
    setResults(null);
  }

  const hasAny =
    results &&
    (results.clients.length > 0 ||
      results.cases.length > 0 ||
      results.contracts.length > 0 ||
      results.commitments.length > 0);

  return (
    // 210px when the top bar has room. When it is tight (a staff bar at 1280px, or in a language
    // with longer labels) the pill gives way, down to the icon, instead of covering the menu beside it.
    <div ref={containerRef} className="relative hidden md:block w-[210px] min-w-[44px] shrink">
      {/* Search input pill. The inner <input> is a natively-focusable control
          and opens the dropdown via its own onFocus, so the wrapper needs no
          click handler or role. */}
      <div
        className="flex items-center gap-2 px-3 h-8 rounded-full transition-colors cursor-text overflow-hidden"
        style={{
          width: '100%',
          border: `1px solid ${open || query ? 'rgba(212,168,75,0.45)' : 'rgba(255,255,255,0.12)'}`,
          background: open || query ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.04)',
        }}
      >
        {/* Magnifying-glass icon */}
        {loading ? (
          <svg
            className="w-3.5 h-3.5 animate-spin flex-shrink-0"
            style={{ color: colors.gold }}
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        ) : (
          <svg
            className="w-3.5 h-3.5 flex-shrink-0"
            style={{ color: colors.gold }}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <circle cx="11" cy="11" r="8" />
            <path strokeLinecap="round" d="M21 21l-4.35-4.35" />
          </svg>
        )}

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search…"
          className="flex-1 bg-transparent outline-none text-sm min-w-0"
          style={{ color: 'rgba(255,255,255,0.85)', caretColor: colors.gold }}
          aria-label="Global search"
          autoComplete="off"
          spellCheck={false}
        />

        {/* Keyboard shortcut hint */}
        {!query && (
          <span
            className="text-xs flex-shrink-0 select-none"
            style={{ color: 'rgba(255,255,255,0.28)' }}
          >
            {isMac ? '⌘K' : 'Ctrl K'}
          </span>
        )}
      </div>

      {/* Results dropdown */}
      {open && query.trim().length >= 2 && (
        <div
          className="absolute right-0 top-full mt-2 rounded-xl overflow-hidden"
          style={{
            width: '340px',
            background: colors.navyLight,
            border: '1px solid rgba(255,255,255,0.07)',
            boxShadow: '0 16px 48px rgba(6,9,26,0.6)',
            zIndex: 50,
          }}
        >
          {loading && !results && (
            <p className="px-4 py-3 text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Searching…
            </p>
          )}

          {!loading && searchError && (
            <p
              role="alert"
              className="px-4 py-3 text-sm"
              style={{ color: colors.gold }}
            >
              {searchError} This is not the same as no match — try again before
              concluding the record does not exist.
            </p>
          )}

          {!loading && !searchError && results && !hasAny && (
            <p className="px-4 py-3 text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
              No results for &ldquo;{query.trim()}&rdquo;
            </p>
          )}

          {results && hasAny && (
            <div className="py-1">
              {/* Clients */}
              {results.clients.length > 0 && (
                <Section label="Clients">
                  {results.clients.map((c) => (
                    <ResultRow
                      key={c.id}
                      primary={c.name}
                      secondary={c.email}
                      icon={<PersonIcon />}
                      onClick={() => navigate_to(`/${prefix}/clients/${c.id}`)}
                    />
                  ))}
                </Section>
              )}

              {/* Cases */}
              {results.cases.length > 0 && (
                <Section label="Onboarding cases">
                  {results.cases.map((c) => (
                    <ResultRow
                      key={c.id}
                      primary={c.client_name}
                      secondary={`${word(c.state_slug)} · ${word(c.status)}`}
                      icon={<CaseIcon />}
                      onClick={() => navigate_to(`/${prefix}/cases/${c.id}`)}
                    />
                  ))}
                </Section>
              )}

              {/* Contracts */}
              {results.contracts.length > 0 && (
                <Section label="Contracts">
                  {results.contracts.map((c) => (
                    <ResultRow
                      key={c.id}
                      primary={c.contract_number}
                      secondary={`${c.client_name} · ${word(c.status)}`}
                      icon={<ContractIcon />}
                      onClick={() => navigate_to(`/${prefix}/clients/${c.client_id}`)}
                    />
                  ))}
                </Section>
              )}

              {/* Commitments */}
              {results.commitments.length > 0 && (
                <Section label="Commitments">
                  {results.commitments.map((c) => (
                    <ResultRow
                      key={c.id}
                      primary={c.client_name}
                      secondary={`${c.product_name} · ${word(c.status)}`}
                      icon={<CommitmentIcon />}
                      onClick={() => navigate_to(`/${prefix}/clients/${c.client_id}`)}
                    />
                  ))}
                </Section>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────

/** A state or status code as words in the reader's language: a row joins it with a name into one
 *  line, which the screen cannot translate as a whole. */
function word(code: string): string {
  return t(humanize(code));
}

function Section({ label, children }: Readonly<{ label: string; children: React.ReactNode }>): JSX.Element {
  return (
    <div>
      <p
        className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider"
        style={{ color: 'rgba(255,255,255,0.3)' }}
      >
        {label}
      </p>
      {children}
    </div>
  );
}

function ResultRow({
  primary,
  secondary,
  icon,
  onClick,
}: Readonly<{
  primary: string;
  secondary: string;
  icon: React.ReactNode;
  onClick: () => void;
}>): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-2 text-left transition-colors group"
      style={{ background: 'transparent' }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.05)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
      }}
    >
      <span className="flex-shrink-0" style={{ color: 'rgba(255,255,255,0.35)' }}>
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span
          className="block text-sm font-medium truncate"
          style={{ color: 'rgba(255,255,255,0.85)' }}
        >
          {primary}
        </span>
        <span className="block text-xs truncate" style={{ color: 'rgba(255,255,255,0.4)' }}>
          {secondary}
        </span>
      </span>
      <svg
        className="w-3.5 h-3.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ color: colors.gold }}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
}

function PersonIcon(): JSX.Element {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
    </svg>
  );
}

function CaseIcon(): JSX.Element {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
    </svg>
  );
}

function ContractIcon(): JSX.Element {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}

function CommitmentIcon(): JSX.Element {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
    </svg>
  );
}

export default GlobalSearch;
